import { NumberFormatter } from '@zervant/reactcomponents'
import * as React from 'react'

import { SubtotalLineInterface } from '../../Interfaces/Draft/SubtotalLineInterface'
import { NumberInput } from '../common/NumberInput'

export interface SubtotalLinePropsInterface extends SubtotalLineInterface {
  onDrop?: (which: number) => void
  onDelete?: (e: Event) => void
  currencyFormat?: NumberFormatter
  automation?: string
  title: string
  columns?: number
  id?: number
  showTaxTotal: boolean
  showNetTotal: boolean
  showGrossTotal?: boolean
  showProductType: boolean
  readonly?: boolean
}

export class SubtotalLine extends React.Component<SubtotalLinePropsInterface, SubtotalLineInterface> {
  public static generateDefaultProps(showNetTotal = false, showTaxTotal = false): SubtotalLinePropsInterface {
    return {
      title: 'Subtotal',
      grossTotal: 0,
      taxTotal: 0,
      netTotal: 0,
      type: 'subtotal',
      showNetTotal,
      showTaxTotal,
      showProductType: false,
    }
  }

  public static defaultProps = SubtotalLine.generateDefaultProps()

  private canDrag: boolean = false

  private reactToDrag(elem: HTMLElement) {
    if (elem.getAttribute('data-type') === 'DragHandle') {
      this.canDrag = true
    }
  }

  private findDropTarget(elem: HTMLElement): HTMLElement {
    while (elem.getAttribute('data-type') !== 'DropTarget') {
      elem = elem.parentElement
    }
    return elem
  }

  public render(): JSX.Element {
    const self = this
    const actions: JSX.Element[] = []

    if (this.props.onDelete && !this.props.readonly) {
      actions.push(
        <span
          className="delete"
          key="onDelete"
          data-automation={this.props.automation + '-delete'}
          onClick={this.props.onDelete.bind(this)}
        ></span>,
      )
    }

    /**
     * The number 5 comes from 2 UI elements (drag handle and delete button) and 3 of many optional columns
     * Changing settings related to the ones below add or remove up to 3 columns
     */
    let colspan = this.props.columns - 5
    if (!this.props.showNetTotal) {
      colspan++
    }
    if (!this.props.showTaxTotal) {
      colspan++
    }
    if (!this.props.showGrossTotal) {
      colspan++
    }
    if (!this.props.showProductType) {
      colspan++
    }

    return (
      <tr
        data-automation={this.props.automation}
        draggable={!this.props.readonly}
        data-type="DropTarget"
        className="Controlled InvoiceSubtotalLine"
        onDragOver={(e) => {
          const elem = self.findDropTarget(e.target as HTMLElement)
          elem.classList.add('Dropping')
          e.preventDefault()
        }}
        onDragLeave={(e) => {
          const elem = self.findDropTarget(e.target as HTMLElement)
          elem.classList.remove('Dropping')
          e.preventDefault()
        }}
        onDrop={(e) => {
          const elem = self.findDropTarget(e.target as HTMLElement)
          elem.classList.remove('Dropping')
          self.props.onDrop(parseInt(e.dataTransfer.getData('id'), 10))
          e.preventDefault()
        }}
        onDragStart={(e) => {
          if (!self.canDrag) {
            e.preventDefault()
          }

          e.dataTransfer.setData('id', '' + self.props.id)
        }}
        onMouseDown={(e) => {
          self.reactToDrag(e.target as HTMLElement)
        }}
      >
        <td data-type="DragHandle" data-automation={this.props.automation + '-dragHandle'} className="dragHandle"></td>
        <td className="title" colSpan={colspan}>
          <hr />
          <span>{this.props.title}</span>
        </td>
        {this.props.showTaxTotal && (
          <td>
            <hr />
            <NumberInput
              formatter={this.props.currencyFormat}
              value={this.props.taxTotal}
              decimals={2}
              borderless={true}
              automation={this.props.automation + '-vat'}
              editable={false}
            />
          </td>
        )}
        {this.props.showNetTotal && (
          <td>
            <hr />
            <NumberInput
              formatter={this.props.currencyFormat}
              value={this.props.netTotal}
              decimals={2}
              borderless={true}
              automation={this.props.automation + '-net'}
              editable={false}
            />
          </td>
        )}
        {this.props.showGrossTotal && (
          <td>
            <hr />
            <NumberInput
              formatter={this.props.currencyFormat}
              value={this.props.grossTotal}
              decimals={2}
              borderless={true}
              automation={this.props.automation + '-total'}
              editable={false}
            />
          </td>
        )}
        <td>
          <hr />
          {actions}
        </td>
      </tr>
    )
  }
}
